import { Component } from "@theme/component";
import { ThemeEvents, MediaStartedPlayingEvent } from "@theme/events";
import { DialogCloseEvent } from "@theme/dialog";

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    this.#ensureMediaLoadedState();
    this.bindVideoPlaybackHandlers();

    if (this.hasAttribute("autoplay")) {
      this.isPlaying = true;
    }

    this.syncPlayPauseHint();
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.#handleMediaStartedPlaying, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {Event} event
   */
  #handleMediaStartedPlaying = (event) => {
    if (event instanceof MediaStartedPlayingEvent && event.detail?.resource === this) return;
    this.pauseMedia();
  };

  /**
   * @returns {boolean}
   */
  #hasPresentMedia() {
    return Boolean(this.querySelector(":scope > video, :scope > iframe[data-video-type]"));
  }

  /**
   * @returns {boolean}
   */
  #isMediaPlaying() {
    const video = this.querySelector("video");
    if (video instanceof HTMLVideoElement) {
      return !video.paused && !video.ended;
    }

    return this.isPlaying;
  }

  /**
   * Marks inline autoplay media as loaded and binds handlers without cloning.
   */
  #ensureMediaLoadedState() {
    if (this.getAttribute("data-media-loaded") === "true" || !this.#hasPresentMedia()) return;

    this.setAttribute("data-media-loaded", "true");
    this.refs.deferredMediaPlayButton?.classList.add("deferred-media__playing");
  }

  /**
   * Reads actual playback state and syncs isPlaying + play/pause hint.
   */
  syncPlayPauseHint() {
    const video = this.querySelector("video");
    if (video instanceof HTMLVideoElement) {
      this.isPlaying = !video.paused && !video.ended;
    }

    if (this.#hasPresentMedia()) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove("hidden");
      toggleMediaButton.classList.toggle("is-playing", isPlaying);
      const playIcon = toggleMediaButton.querySelector(".icon-play");
      if (playIcon) playIcon.classList.toggle("hidden", isPlaying);
      const pauseIcon = toggleMediaButton.querySelector(".icon-pause");
      if (pauseIcon) pauseIcon.classList.toggle("hidden", !isPlaying);

      // Auto-hide pause icon after 0.5s of playing; show instantly when paused
      clearTimeout(this._idleTimer);
      toggleMediaButton.classList.remove("vp-idle");
      if (isPlaying) {
        this._idleTimer = setTimeout(() => {
          toggleMediaButton.classList.add("vp-idle");
        }, 500);
      }
    }
  }

  /**
   * Shows the deferred media content and starts playback (single click).
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.playMedia();
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute("data-media-loaded")) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector("template")?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute("data-media-loaded", "true");
    this.appendChild(content);
    this.bindVideoPlaybackHandlers();

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add("deferred-media__playing");

    if (content instanceof HTMLVideoElement && content.hasAttribute("autoplay")) {
      // force autoplay for safari
      content
        .play()
        ?.then(() => this.syncPlayPauseHint())
        .catch(() => this.syncPlayPauseHint());
    } else {
      this.syncPlayPauseHint();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (!this.getAttribute("data-media-loaded")) {
      if (this.#hasPresentMedia()) {
        this.#ensureMediaLoadedState();
        this.bindVideoPlaybackHandlers();
      } else {
        this.loadContent(false);
      }
    }

    if (this.#isMediaPlaying()) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  /**
   * Binds play/pause/ended listeners so the hint stays in sync with native video state.
   */
  bindVideoPlaybackHandlers() {
    const video = this.querySelector("video");
    if (!(video instanceof HTMLVideoElement) || video.dataset.playbackListenerBound === "true") return;

    video.dataset.playbackListenerBound = "true";
    const signal = this.#abortController.signal;
    video.addEventListener("play", () => this.syncPlayPauseHint(), { signal });
    video.addEventListener("pause", () => this.syncPlayPauseHint(), { signal });
    video.addEventListener("ended", this.resetToInitialState.bind(this), { signal });
  }

  /**
   * Restores media to initial UI state.
   */
  resetToInitialState() {
    this.isPlaying = false;
    this.updatePlayPauseHint(false);
    this.refs.deferredMediaPlayButton?.classList.remove("deferred-media__playing");

    /** @type {HTMLVideoElement | null} */
    const video = this.querySelector("video");
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }

  async playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector("iframe[data-video-type]");
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        "*",
      );
      this.isPlaying = true;
      this.syncPlayPauseHint();
    } else {
      const video = this.querySelector("video");
      if (video) {
        try {
          // On iOS, video may not be ready immediately after being appended to the DOM.
          // Wait for canplay before calling play() to avoid a silent rejection.
          if (video.readyState < 2) {
            await new Promise((resolve) => video.addEventListener("canplay", resolve, { once: true }));
          }
          await video.play();
          this.isPlaying = true;
        } catch (_) {
          // Play rejected — keep UI in paused state
          this.isPlaying = false;
        }
        this.syncPlayPauseHint();
      }
    }
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector("iframe[data-video-type]");

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"' + "pauseVideo" + '","args":""}'
          : '{"method":"pause"}',
        "*",
      );
    } else {
      this.querySelector("video")?.pause();
    }
    this.isPlaying = false;
    this.syncPlayPauseHint();
  }
}

if (!customElements.get("deferred-media")) {
  customElements.define("deferred-media", DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  async playMedia() {
    await super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector("model-viewer");
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      "pointerdown",
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal },
    );

    element.addEventListener(
      "click",
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal },
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get("product-model")) {
  customElements.define("product-model", ProductModel);
}
